import React, { createContext, useContext, useEffect, useState } from 'react'

const ThemeContext = createContext(null)

// FOUC prevention - this must be run as early as possible
export const initializeTheme = () => {
  if (typeof window === 'undefined') return
  
  const script = `
    (() => {
      const key = 'fk-theme';
      const saved = localStorage.getItem(key);
      if (saved === 'light' || saved === 'dark') {
        document.documentElement.setAttribute('data-theme', saved);
      } else {
        // Respect OS preference if no saved theme
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
      }
    })();
  `
  
  // If this is being called after DOM is ready, execute immediately
  if (document.readyState !== 'loading') {
    const fn = new Function(script.replace(/^\s*\(\(\) => \{|\}\)\(\);\s*$/g, ''))
    fn()
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    // Check for saved theme
    const saved = localStorage.getItem('fk-theme')
    if (saved === 'light' || saved === 'dark') {
      return saved
    }
    
    // Check OS preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    
    return 'light'
  })

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme)
    
    // Save to localStorage
    localStorage.setItem('fk-theme', theme)
  }, [theme])

  useEffect(() => {
    // Listen for OS theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = (e) => {
      // Only auto-update if user hasn't manually set a preference
      const saved = localStorage.getItem('fk-theme-manual')
      if (!saved) {
        setTheme(e.matches ? 'dark' : 'light')
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
    // Mark as manually set
    localStorage.setItem('fk-theme-manual', 'true')
  }

  const setThemeExplicit = (newTheme) => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setTheme(newTheme)
      localStorage.setItem('fk-theme-manual', 'true')
    }
  }

  const resetToSystem = () => {
    localStorage.removeItem('fk-theme-manual')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    setTheme(prefersDark ? 'dark' : 'light')
  }

  const value = {
    theme,
    toggleTheme,
    setTheme: setThemeExplicit,
    resetToSystem,
    isDark: theme === 'dark',
    isLight: theme === 'light'
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}